import * as React from "react";
import { format } from "date-fns";
import { Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface MonthYearPickerProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  fromYear?: number;
  toYear?: number;
  className?: string;
  placeholder?: string;
}

const MonthYearPicker = React.forwardRef<
  HTMLButtonElement,
  MonthYearPickerProps
>(({ 
  selectedDate, 
  onDateChange, 
  fromYear = 2020, 
  toYear = 2030, 
  className,
  placeholder = "Select month"
}, ref) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [tempMonth, setTempMonth] = React.useState(selectedDate.getMonth());
  const [tempYear, setTempYear] = React.useState(selectedDate.getFullYear());

  // Generate month options
  const months = Array.from({ length: 12 }, (_, i) => ({
    value: i,
    label: format(new Date(2024, i, 1), 'MMMM')
  }));

  // Generate year options
  const years = Array.from({ length: toYear - fromYear + 1 }, (_, i) => ({
    value: fromYear + i,
    label: (fromYear + i).toString()
  }));

  // Update temp values when selectedDate changes
  React.useEffect(() => {
    setTempMonth(selectedDate.getMonth());
    setTempYear(selectedDate.getFullYear());
  }, [selectedDate]);

  const handleMonthChange = (monthStr: string) => {
    const month = parseInt(monthStr);
    setTempMonth(month);
    
    // Create new date and update
    const newDate = new Date(tempYear, month, 1);
    onDateChange(newDate);
    setIsOpen(false);
  };

  const handleYearChange = (yearStr: string) => {
    const year = parseInt(yearStr);
    setTempYear(year);
    
    // Create new date and update
    const newDate = new Date(year, tempMonth, 1);
    onDateChange(newDate);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          className={cn("justify-start text-left font-normal", className)}
        >
          <Calendar className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, "MMMM yyyy") : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4" align="start">
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Month</label>
            <Select value={tempMonth.toString()} onValueChange={handleMonthChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month.value} value={month.value.toString()}>
                    {month.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Year</label>
            <Select value={tempYear.toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((year) => (
                  <SelectItem key={year.value} value={year.value.toString()}>
                    {year.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

MonthYearPicker.displayName = "MonthYearPicker";

export { MonthYearPicker };
